import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { toast } from 'react-toastify';
import { 
  Plus, Search, Edit2, Trash2, BookOpen, Calendar, Eye,
  School, Clock,  FileText, 
  Bookmark, User, Tag,
  ArrowLeft
} from 'lucide-react';
import api from '../components/api';
import Modal from '../components/Modal';
import FormInput from '../components/FormInput';
import { useNavigate } from 'react-router-dom';
import { getDeleteConfirmation } from '../utils/confirmationUtils';
import ConfirmationModal from '../components/ConfirmationModal';
import Pagination from '../components/Pagination';

// Composant pour les onglets
const TabButton = ({ isActive, onClick, icon: Icon, children }) => (
  <button
    onClick={onClick}
    className={`
      flex-1 px-4 py-4 text-sm font-medium relative transition-all duration-300 ease-in-out
      ${isActive 
        ? 'text-[#202654] bg-white' 
        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
      }
    `}
  >
    <div className="flex items-center justify-center space-x-2">
      <Icon className={`h-5 w-5 transition-colors duration-300 ${isActive ? 'text-[#202654]' : 'text-gray-400'}`} />
      <span>{children}</span>
    </div>
    {isActive && (
      <div className="absolute bottom-0 left-0 w-full h-0.5 bg-[#202654] transform scale-x-100 transition-transform duration-300" />
    )}
  </button>
);

const ModulePage = () => {
  const navigate = useNavigate();
  const [modules, setModules] = useState([]);
  const [semestres, setSemestres] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedModule, setSelectedModule] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    libelle: '',
    semestre: '',
  });
  const [formErrors, setFormErrors] = useState({});
  
  // États pour la confirmation de suppression
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [moduleToDelete, setModuleToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // États pour les onglets
  const [activeTab, setActiveTab] = useState('modules');
  
  // États pour le second onglet (modules par classe)
  const [moduleClasses, setModuleClasses] = useState([]);
  const [classes, setClasses] = useState([]);
  const [enseignants, setEnseignants] = useState([]);
  const [loadingModuleClasses, setLoadingModuleClasses] = useState(false);
  const [searchTermModuleClasses, setSearchTermModuleClasses] = useState('');
  const [isModuleClasseModalOpen, setIsModuleClasseModalOpen] = useState(false);
  const [selectedModuleClasse, setSelectedModuleClasse] = useState(null);
  const [formDataModuleClasse, setFormDataModuleClasse] = useState({
    module: '',
    classe: '',
    nombre_heure: '',
    coeficient: '',
    nombre_credit: '',
    note_validation: '',
    enseignant: ''
  });
  const [formErrorsModuleClasse, setFormErrorsModuleClasse] = useState({});
  const [isModuleClasseConfirmModalOpen, setIsModuleClasseConfirmModalOpen] = useState(false);
  const [moduleClasseToDelete, setModuleClasseToDelete] = useState(null);
  const [deleteLoadingModuleClasse, setDeleteLoadingModuleClasse] = useState(false);

  // États pour la pagination
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: 1,
    count: 0
  });
  
  const [moduleClassePagination, setModuleClassePagination] = useState({
    current_page: 1,
    total_pages: 1,
    count: 0
  });

  useEffect(() => {
    fetchInitialData();
    
    // Récupérer l'onglet actif depuis le localStorage
    const savedTab = localStorage.getItem('moduleActiveTab');
    if (savedTab) {
      setActiveTab(savedTab);
    }
  }, []);

  // Mettre à jour le localStorage quand l'onglet change
  useEffect(() => {
    localStorage.setItem('moduleActiveTab', activeTab);
    
    // Charger les données spécifiques à l'onglet
    if (activeTab === 'moduleClasses' && moduleClasses.length === 0) {
      fetchModuleClasses();
      fetchClasses();
      fetchEnseignants();
    }
  }, [activeTab]);

  const fetchInitialData = async (page = 1) => {
    try {
      setLoading(true);
      const [modulesRes, semestresRes, classesRes, enseignantsRes] = await Promise.all([
        api.get(`/matiere/modules/?page=${page}`),
        api.get('/emplois/semestres/actifs/'),
        api.get('/classe/get-all-classe/'),
        api.get('/enseignant/get-all-enseignant/')
      ]);

      setModules(modulesRes.data.results);
      setPagination({
        current_page: modulesRes.data.current_page,
        total_pages: modulesRes.data.total_pages,
        count: modulesRes.data.count
      });
      setSemestres(semestresRes.data);
      setClasses(classesRes.data);
      setEnseignants(enseignantsRes.data);
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const fetchModuleClasses = async (page = 1) => {
    try {
      setLoadingModuleClasses(true);
      const response = await api.get(`/matiere/module-classes/?page=${page}`);
      setModuleClasses(response.data.results);
      setModuleClassePagination({
        current_page: response.data.current_page,
        total_pages: response.data.total_pages,
        count: response.data.count
      });
      setLoadingModuleClasses(false);
    } catch (error) {
      console.error('Erreur lors du chargement des modules par classe:', error);
      toast.error('Erreur lors du chargement des modules par classe');
      setLoadingModuleClasses(false);
    }
  };

  const fetchClasses = async () => {
    try {
      const response = await api.get('/classe/get-all-classe/');
      setClasses(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des classes:', error);
      toast.error('Erreur lors du chargement des classes');
    }
  };

  const fetchEnseignants = async () => {
    try {
      const response = await api.get('/enseignant/get-all-enseignant/');
      setEnseignants(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des enseignants:', error);
      toast.error('Erreur lors du chargement des enseignants');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    
    // Clear error when field is edited
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: undefined
      });
    }
  };

  const handleOpenModal = (module = null) => {
    if (module) {
      setFormData({
        libelle: module.libelle,
        semestre: module.semestre?.id || '',
      });
      setSelectedModule(module);
    } else {
      setFormData({
        libelle: '',
        semestre: '',
      });
      setSelectedModule(null);
    }
    setFormErrors({});
    setIsModalOpen(true);
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.libelle.trim()) {
      errors.libelle = 'Le libellé du module est requis';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    try {
      if (selectedModule) {
        await api.put(`/matiere/modules/${selectedModule.id}/`, formData);
        toast.success('Module modifié avec succès');
      } else {
        await api.post('/matiere/modules/', formData);
        toast.success('Module créé avec succès');
      }
      setIsModalOpen(false);
      fetchInitialData();
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      
      if (error.response?.data) {
        // Transform API validation errors to match our form
        const apiErrors = {};
        Object.entries(error.response.data).forEach(([field, messages]) => {
          apiErrors[field] = Array.isArray(messages) ? messages[0] : messages;
        });
        setFormErrors(apiErrors);
      } else {
        toast.error('Erreur lors de la sauvegarde du module');
      }
    }
  };

  const handleOpenDeleteModal = (module) => {
    setModuleToDelete(module);
    setIsConfirmModalOpen(true);
  };

  const handleDelete = async () => {
    if (!moduleToDelete) return;
    
    setDeleteLoading(true);
    try {
      await api.delete(`/matiere/modules/${moduleToDelete.id}/`);
      toast.success('Module supprimé avec succès');
      fetchInitialData();
      setIsConfirmModalOpen(false);
      setModuleToDelete(null);
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      toast.error('Erreur lors de la suppression du module');
    } finally {
      setDeleteLoading(false);
    }
  };

  // Utiliser la fonction utilitaire pour obtenir la configuration
  const confirmConfig = moduleToDelete 
    ? getDeleteConfirmation(moduleToDelete, 'le module', 'libelle', 'supprimer') 
    : {};

  const handleViewModuleDetails = (moduleId) => {
    navigate(`/detail-module/${moduleId}`);
  };

  const renderModalFooter = () => (
    <div className="flex justify-end space-x-3">
      <button
        type="button"
        onClick={() => setIsModalOpen(false)}
        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#202654]"
      >
        Annuler
      </button>
      <button
        type="submit"
        onClick={handleSubmit}
        className="px-4 py-2 text-sm font-medium text-white bg-[#202654] border border-transparent rounded-md shadow-sm hover:bg-[#202654]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#202654]"
      >
        {selectedModule ? 'Modifier' : 'Créer'}
      </button>
    </div>
  );

  // Méthodes pour l'onglet Module par classe
  const handleModuleClasseInputChange = (e) => {
    const { name, value } = e.target;
    setFormDataModuleClasse({ ...formDataModuleClasse, [name]: value });
    
    // Clear error when field is edited
    if (formErrorsModuleClasse[name]) {
      setFormErrorsModuleClasse({
        ...formErrorsModuleClasse,
        [name]: undefined
      });
    }
  };

  const handleOpenModuleClasseModal = (moduleClasse = null) => {
    if (moduleClasse) {
      setFormDataModuleClasse({
        module: moduleClasse.module?.id || '',
        classe: moduleClasse.classe?.id || '',
        nombre_heure: moduleClasse.nombre_heure || '',
        coeficient: moduleClasse.coeficient || '',
        nombre_credit: moduleClasse.nombre_credit || '',
        note_validation: moduleClasse.note_validation || '',
        enseignant: moduleClasse.enseignant?.id || ''
      });
      setSelectedModuleClasse(moduleClasse);
    } else {
      setFormDataModuleClasse({
        module: '',
        classe: '',
        nombre_heure: '',
        coeficient: '',
        nombre_credit: '',
        note_validation: '',
        enseignant: ''
      });
      setSelectedModuleClasse(null);
    }
    setFormErrorsModuleClasse({});
    setIsModuleClasseModalOpen(true);
  };

  const validateModuleClasseForm = () => {
    const errors = {};
    
    if (!formDataModuleClasse.module) {
      errors.module = 'Le module est requis';
    }
    
    if (!formDataModuleClasse.classe) {
      errors.classe = 'La classe est requise';
    }
    
    if (!formDataModuleClasse.nombre_heure) {
      errors.nombre_heure = 'Le nombre d\'heures est requis';
    } else if (parseInt(formDataModuleClasse.nombre_heure) <= 0) {
      errors.nombre_heure = 'Le nombre d\'heures doit être positif';
    }
    
    if (!formDataModuleClasse.coeficient) {
      errors.coeficient = 'Le coefficient est requis';
    } else if (parseInt(formDataModuleClasse.coeficient) <= 0) {
      errors.coeficient = 'Le coefficient doit être positif';
    }
    
    // Validation des champs optionnels uniquement s'ils sont remplis
    if (formDataModuleClasse.nombre_credit && formDataModuleClasse.nombre_credit.trim() !== '') {
      const creditValue = parseInt(formDataModuleClasse.nombre_credit);
      if (isNaN(creditValue) || creditValue < 0) {
        errors.nombre_credit = 'Le nombre de crédits doit être un nombre positif';
      }
    }
    
    if (formDataModuleClasse.note_validation && formDataModuleClasse.note_validation.trim() !== '') {
      const note = parseFloat(formDataModuleClasse.note_validation);
      if (isNaN(note) || note < 0 || note > 20) {
        errors.note_validation = 'La note doit être comprise entre 0 et 20';
      }
    }
    
    setFormErrorsModuleClasse(errors);
    return Object.keys(errors).length === 0;
  };

  const handleModuleClasseSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateModuleClasseForm()) return;
    
    // Correction : nombre_credit à 0 si vide
    const dataToSend = { ...formDataModuleClasse };
    if (!dataToSend.nombre_credit || dataToSend.nombre_credit === "") {
      dataToSend.nombre_credit = 0;
    }
    
    try {
      if (selectedModuleClasse) {
        await api.put(`/matiere/module-classes/${selectedModuleClasse.id}/`, dataToSend);
        toast.success('Affectation modifiée avec succès');
        setIsModuleClasseModalOpen(false);
        fetchModuleClasses();
      } else {
        await api.post('/matiere/module-classes/', dataToSend);
        toast.success('Affectation créée avec succès');
      setIsModuleClasseModalOpen(false);
      fetchModuleClasses();
      }
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      
      if (error.response?.data) {
        const apiErrors = {};
        let mainErrorMessage = null;
        
        // Traiter les erreurs de l'API - ne montrer qu'un seul toast
        if (typeof error.response.data === 'object') {
          // Collecter toutes les erreurs sans afficher de toast
        Object.entries(error.response.data).forEach(([field, messages]) => {
            // Pour les erreurs de champs spécifiques
            if (Array.isArray(messages)) {
              apiErrors[field] = messages[0]; // Prendre le premier message d'erreur
              // Garder le premier message comme message principal s'il n'est pas déjà défini
              if (!mainErrorMessage) {
                mainErrorMessage = `${messages[0]}`;
              }
            } else if (typeof messages === 'string') {
              apiErrors[field] = messages;
              if (!mainErrorMessage) {
                mainErrorMessage = `${messages}`;
              }
            }
          });
        } else if (typeof error.response.data === 'string') {
          // Si c'est une simple chaîne d'erreur
          apiErrors['non_field_errors'] = error.response.data;
          mainErrorMessage = error.response.data;
        }
        
        // Si on a une erreur non liée à un champ spécifique
        if (error.response.data.non_field_errors) {
          const nonFieldErrors = error.response.data.non_field_errors;
          if (Array.isArray(nonFieldErrors)) {
            apiErrors['non_field_errors'] = nonFieldErrors[0];
            mainErrorMessage = nonFieldErrors[0];
          } else {
            apiErrors['non_field_errors'] = nonFieldErrors;
            mainErrorMessage = nonFieldErrors;
          }
        }
        
        // Si on a un message d'erreur général
        if (error.response.data.detail) {
          apiErrors['non_field_errors'] = error.response.data.detail;
          mainErrorMessage = error.response.data.detail;
        }
        
        // N'afficher qu'un seul toast pour l'erreur principale
        if (mainErrorMessage) {
          toast.error(mainErrorMessage);
        } else {
          toast.error("Erreur lors de la sauvegarde de l'affectation");
        }
        
        // Mettre à jour les erreurs de formulaire pour affichage inline
        setFormErrorsModuleClasse(apiErrors);
      } else {
        // Erreur générale
        toast.error('Erreur lors de la sauvegarde de l\'affectation. Veuillez réessayer.');
      }
    }
  };

  const handleOpenModuleClasseDeleteModal = (moduleClasse) => {
    setModuleClasseToDelete(moduleClasse);
    setIsModuleClasseConfirmModalOpen(true);
  };

  const handleModuleClasseDelete = async () => {
    if (!moduleClasseToDelete) return;
    
    setDeleteLoadingModuleClasse(true);
    try {
      await api.delete(`/matiere/module-classes/${moduleClasseToDelete.id}/`);
      toast.success('Affectation supprimée avec succès');
      fetchModuleClasses();
      setIsModuleClasseConfirmModalOpen(false);
      setModuleClasseToDelete(null);
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      toast.error('Erreur lors de la suppression de l\'affectation');
    } finally {
      setDeleteLoadingModuleClasse(false);
    }
  };

  // Utiliser la fonction utilitaire pour obtenir la configuration de suppression de module classe
  const confirmModuleClasseConfig = moduleClasseToDelete 
    ? getDeleteConfirmation(moduleClasseToDelete, 'l\'affectation', 'id', 'supprimer') 
    : {};



  const renderModuleClasseModalFooter = () => (
    <div className="flex justify-end space-x-3">
      <button
        type="button"
        onClick={() => setIsModuleClasseModalOpen(false)}
        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#202654]"
      >
        Annuler
      </button>
      <button
        type="submit"
        onClick={handleModuleClasseSubmit}
        className="px-4 py-2 text-sm font-medium text-white bg-[#202654] border border-transparent rounded-md shadow-sm hover:bg-[#202654]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#202654]"
      >
        {selectedModuleClasse ? 'Modifier' : 'Créer'}
      </button>
    </div>
  );

  // Gestionnaires de changement de page
  const handlePageChange = (page) => {
    fetchInitialData(page);
  };

  const handleModuleClassePageChange = (page) => {
    fetchModuleClasses(page);
  };

  // Filtrer les modules en fonction du terme de recherche
  const filteredModules = modules.filter(module =>
    module.libelle.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Filtrer les modules par classe en fonction du terme de recherche
  const filteredModuleClasses = moduleClasses.filter(moduleClasse =>
    moduleClasse.module?.libelle?.toLowerCase().includes(searchTermModuleClasses.toLowerCase()) ||
    moduleClasse.classe?.libelle?.toLowerCase().includes(searchTermModuleClasses.toLowerCase()) ||
    (moduleClasse.enseignant?.nom + " " + moduleClasse.enseignant?.prenom).toLowerCase().includes(searchTermModuleClasses.toLowerCase())
  );

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-8">
          {/* Card principale avec design professionnel */}
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
            {/* Header avec bannière bleue */}
            <div className="bg-gradient-to-r from-[#202654] to-[#202654]/90 px-6 py-8">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                <div className="flex items-center text-white">
                  <button
                    onClick={() => navigate(-1)}
                    className="mr-4 p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-all duration-200 backdrop-blur-sm"
                  >
                    <ArrowLeft className="h-5 w-5" />
                  </button>
                  <div className="bg-white/10 p-3 rounded-xl mr-4">
                    <BookOpen className="h-8 w-8 text-white" />
                  </div>
                  <div>
                    <h1 className="text-2xl font-bold">Gestion des Modules</h1>
                    <p className="mt-1 text-blue-100">
                      Gérez les modules et leurs affectations aux classes
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contenu principal */}
            <div className="p-6">
              <div className="max-w-7xl mx-auto">
                {/* Contenu existant */}
                <div className="flex justify-between items-center mb-6">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">Liste des modules</h2>
                    <p className="mt-1 text-sm text-gray-600">
                      Gérez les modules et leurs affectations aux classes
                    </p>
                  </div>
                  <button
                    onClick={() => activeTab === 'modules' ? handleOpenModal() : handleOpenModuleClasseModal()}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#202654] hover:bg-[#202654]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#202654]"
                  >
                    <Plus className="h-5 w-5 mr-2" />
                    {activeTab === 'modules' ? 'Nouveau Module' : 'Nouvelle Affectation'}
                  </button>
                </div>

                {/* Onglets améliorés */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
                  <nav className="flex">
                    <TabButton
                      isActive={activeTab === 'modules'}
                      onClick={() => setActiveTab('modules')}
                      icon={BookOpen}
                    >
                      Modules
                    </TabButton>
                    <TabButton
                      isActive={activeTab === 'moduleClasses'}
                      onClick={() => setActiveTab('moduleClasses')}
                      icon={School}
                    >
                      Modules par classe
                    </TabButton>
                  </nav>
                </div>

                {/* Contenu de l'onglet actif */}
                {activeTab === 'modules' ? (
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    <div className="p-6 border-b border-gray-200">
                      <div className="flex items-center">
                        <div className="relative flex-1">
                          <input
                            type="text"
                            placeholder="Rechercher un module..."
                            className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-[#202654] focus:border-[#202654]"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                          />
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <Search className="h-5 w-5 text-gray-400" />
                          </div>
                        </div>
                      </div>
                    </div>

                    {loading ? (
                      <div className="flex justify-center items-center p-8">
                        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#202654]"></div>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Module
                              </th>
                              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Semestre
                              </th>
                              <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {filteredModules.length === 0 ? (
                              <tr>
                                <td colSpan="3" className="px-6 py-12">
                                  <div className="flex flex-col items-center justify-center text-center">
                                    <BookOpen className="h-12 w-12 text-gray-400 mb-4" />
                                    <h3 className="text-lg font-medium text-gray-900 mb-1">Aucun module trouvé</h3>
                                    <p className="text-sm text-gray-500 max-w-md">
                                      {searchTerm ? 'Essayez avec un autre terme de recherche' : 'Commencez par créer votre premier module'}
                                    </p>
                                    {!searchTerm && (
                                      <button
                                        onClick={() => handleOpenModal()}
                                        className="mt-6 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#202654] hover:bg-[#202654]/90"
                                      >
                                        <Plus className="h-5 w-5 mr-2" />
                                        Créer un module
                                      </button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ) : (
                              filteredModules.map((module) => (
                                <tr key={module.id} className="hover:bg-gray-50 transition-colors">
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="flex items-center">
                                      <div className="flex-shrink-0 h-10 w-10">
                                        <div className="h-10 w-10 rounded-full bg-[#202654] flex items-center justify-center">
                                          <BookOpen className="h-6 w-6 text-white" />
                                        </div>
                                      </div>
                                      <div className="ml-4">
                                        <div className="text-sm font-medium text-gray-900">{module.libelle}</div>
                                      </div>
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                      {module.semestre?.libelle || 'Non assigné'}
                                    </span>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                    <div className="flex justify-end space-x-2">
                                      <button
                                        onClick={() => handleViewModuleDetails(module.id)}
                                        className="text-[#202654] hover:text-[#202654]/80"
                                      >
                                        <Eye className="h-5 w-5" />
                                      </button>
                                      <button
                                        onClick={() => handleOpenModal(module)}
                                        className="text-[#202654] hover:text-[#202654]/80"
                                      >
                                        <Edit2 className="h-5 w-5" />
                                      </button>
                                      <button
                                        onClick={() => handleOpenDeleteModal(module)}
                                        className="text-red-600 hover:text-red-800"
                                      >
                                        <Trash2 className="h-5 w-5" />
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {/* Pagination */}
                    {pagination.total_pages > 1 && (
                      <div className="px-6 py-3 border-t border-gray-200">
                        <Pagination
                          currentPage={pagination.current_page}
                          totalPages={pagination.total_pages}
                          onPageChange={handlePageChange}
                        />
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    <div className="p-6 border-b border-gray-200">
                      <div className="flex items-center">
                        <div className="relative flex-1">
                          <input
                            type="text"
                            placeholder="Rechercher par module, classe ou enseignant..."
                            className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-[#202654] focus:border-[#202654]"
                            value={searchTermModuleClasses}
                            onChange={(e) => setSearchTermModuleClasses(e.target.value)}
                          />
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <Search className="h-5 w-5 text-gray-400" />
                          </div>
                        </div>
                      </div>
                    </div>

                    {loadingModuleClasses ? (
                      <div className="flex justify-center items-center p-8">
                        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#202654]"></div>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Module
                              </th>
                              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Classe
                              </th>
                              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Heures
                              </th>
                              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Coefficient
                              </th>
                              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Enseignant
                              </th>
                              <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {filteredModuleClasses.length === 0 ? (
                              <tr>
                                <td colSpan="6" className="px-6 py-12">
                                  <div className="flex flex-col items-center justify-center text-center">
                                    <School className="h-12 w-12 text-gray-400 mb-4" />
                                    <h3 className="text-lg font-medium text-gray-900 mb-1">Aucune affectation trouvée</h3>
                                    <p className="text-sm text-gray-500 max-w-md">
                                      {searchTermModuleClasses ? 'Essayez avec un autre terme de recherche' : 'Commencez par affecter un module à une classe'}
                                    </p>
                                    {!searchTermModuleClasses && (
                                      <button
                                        onClick={() => handleOpenModuleClasseModal()}
                                        className="mt-6 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#202654] hover:bg-[#202654]/90"
                                      >
                                        <Plus className="h-5 w-5 mr-2" />
                                        Créer une affectation
                                      </button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ) : (
                              filteredModuleClasses.map((moduleClasse) => (
                                <tr key={moduleClasse.id} className="hover:bg-gray-50 transition-colors">
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="flex items-center">
                                      <div className="h-9 w-9 rounded-full bg-[#202654]/10 flex items-center justify-center mr-3">
                                        <BookOpen className="h-5 w-5 text-[#202654]" />
                                      </div>
                                      <div>
                                        <div className="text-sm font-medium text-gray-900">
                                          {moduleClasse.module?.libelle || 'N/A'}
                                        </div>
                                        {moduleClasse.module?.semestre && (
                                          <div className="text-xs text-gray-500">
                                            {moduleClasse.module.semestre.libelle}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="flex items-center">
                                      <div className="h-8 w-8 rounded-full bg-indigo-100 flex items-center justify-center mr-2">
                                        <School className="h-4 w-4 text-indigo-600" />
                                      </div>
                                      <div>
                                        <div className="text-sm font-medium text-gray-900">
                                          {moduleClasse.classe?.libelle || 'N/A'}
                                        </div>
                                        <div className="text-xs text-gray-500">
                                          {moduleClasse.classe?.niveau?.libelle} - {moduleClasse.classe?.filiere?.libelle}
                                        </div>
                                      </div>
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <span className="px-2.5 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                                      {moduleClasse.nombre_heure} h
                                    </span>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <span className="px-2.5 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-purple-100 text-purple-800">
                                      Coef. {moduleClasse.coeficient}
                                    </span>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    {moduleClasse.enseignant ? (
                                      <div className="flex items-center">
                                        <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center mr-2">
                                          <User className="h-4 w-4 text-gray-500" />
                                        </div>
                                        <span className="text-sm text-gray-900">{moduleClasse.enseignant.nom + " " + moduleClasse.enseignant.prenom}</span>
                                      </div>
                                    ) : (
                                      <span className="text-sm text-gray-500">Non assigné</span>
                                    )}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                    <button
                                      onClick={() => handleOpenModuleClasseModal(moduleClasse)}
                                      className="text-indigo-600 hover:text-indigo-900 mr-4 p-1 rounded-full hover:bg-indigo-50 transition-colors"
                                      title="Modifier"
                                    >
                                      <Edit2 className="h-5 w-5" />
                                    </button>
                                    <button
                                      onClick={() => handleOpenModuleClasseDeleteModal(moduleClasse)}
                                      className="text-red-600 hover:text-red-900 p-1 rounded-full hover:bg-red-50 transition-colors"
                                      title="Supprimer"
                                    >
                                      <Trash2 className="h-5 w-5" />
                                    </button>
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {/* Pagination */}
                    {moduleClassePagination.total_pages > 1 && (
                      <div className="px-6 py-3 border-t border-gray-200">
                        <Pagination
                          currentPage={moduleClassePagination.current_page}
                          totalPages={moduleClassePagination.total_pages}
                          onPageChange={handleModuleClassePageChange}
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Modals existants */}
        {/* Modal pour création/édition de module */}
        <Modal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          title={selectedModule ? 'Modifier le module' : 'Nouveau module'}
          footer={renderModalFooter()}
        >
          <form onSubmit={handleSubmit} className="space-y-6">
            <FormInput
              label="Libellé du module"
              type="text"
              name="libelle"
              value={formData.libelle}
              onChange={handleInputChange}
              placeholder="Entrez le nom du module"
              required
              error={formErrors.libelle}
              icon={BookOpen}
            />
            
            <FormInput
              label="Semestre"
              type="select"
              name="semestre"
              value={formData.semestre}
              onChange={handleInputChange}
              icon={Calendar}
              options={[
                { value: '', label: 'Sélectionner un semestre' },
                ...semestres.map(semestre => ({ value: semestre.id, label: semestre.libelle }))
              ]}
              helpText="Facultatif. Vous pouvez associer ce module à un semestre."
              error={formErrors.semestre}
            />
          </form>
        </Modal>

        {/* Modal pour les modules par classe */}
        <Modal
          isOpen={isModuleClasseModalOpen}
          onClose={() => setIsModuleClasseModalOpen(false)}
          title={selectedModuleClasse ? 'Modifier l\'affectation' : 'Nouvelle affectation de module'}
          footer={renderModuleClasseModalFooter()}
        >
          <form onSubmit={handleModuleClasseSubmit} className="space-y-6">
            <FormInput
              label="Module"
              type="select"
              name="module"
              value={formDataModuleClasse.module}
              onChange={handleModuleClasseInputChange}
              options={[
                { value: '', label: 'Sélectionner un module' },
                ...modules.map(module => ({ value: module.id, label: module.libelle }))
              ]}
              required
              error={formErrorsModuleClasse.module}
              icon={BookOpen}
            />
            
            <FormInput
              label="Classe"
              type="select"
              name="classe"
              value={formDataModuleClasse.classe}
              onChange={handleModuleClasseInputChange}
              options={[
                { value: '', label: 'Sélectionner une classe' },
                ...classes.map(classe => ({ 
                  value: classe.id, 
                  label: `${classe.libelle} (${classe.niveau?.libelle} - ${classe.filiere?.libelle})` 
                }))
              ]}
              required
              error={formErrorsModuleClasse.classe}
              icon={School}
            />
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormInput
                label="Nombre d'heures"
                type="number"
                name="nombre_heure"
                value={formDataModuleClasse.nombre_heure}
                onChange={handleModuleClasseInputChange}
                placeholder="Ex: 30"
                required
                min="1"
                error={formErrorsModuleClasse.nombre_heure}
                icon={Clock}
              />
              
              <FormInput
                label="Coefficient"
                type="number"
                name="coeficient"
                value={formDataModuleClasse.coeficient}
                onChange={handleModuleClasseInputChange}
                placeholder="Ex: 2"
                required
                min="1"
                error={formErrorsModuleClasse.coeficient}
                icon={Tag}
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormInput
                label="Nombre de crédits"
                type="number"
                name="nombre_credit"
                value={formDataModuleClasse.nombre_credit}
                onChange={handleModuleClasseInputChange}
                placeholder="Ex: 3"
                min="0"
                helpText="Optionnel"
                error={formErrorsModuleClasse.nombre_credit}
                icon={Bookmark}
              />
              
              <FormInput
                label="Note de validation"
                type="number"
                name="note_validation"
                value={formDataModuleClasse.note_validation}
                onChange={handleModuleClasseInputChange}
                placeholder="Ex: 10"
                min="0"
                max="20"
                helpText="Optionnel"
                error={formErrorsModuleClasse.note_validation}
                icon={FileText}
              />
            </div>
            
            <FormInput
              label="Enseignant"
              type="select"
              name="enseignant"
              value={formDataModuleClasse.enseignant}
              onChange={handleModuleClasseInputChange}
              options={[
                { value: '', label: 'Sélectionner un enseignant (optionnel)' },
                ...enseignants.map(enseignant => ({ 
                  value: enseignant.id, 
                  label: enseignant.nom + " " + enseignant.prenom
                }))
              ]}
              error={formErrorsModuleClasse.enseignant}
              icon={User}
              helpText="Vous pouvez assigner un enseignant maintenant ou plus tard"
            />
          </form>
        </Modal>

        {/* Modal de confirmation de suppression de module */}
        <ConfirmationModal 
          isOpen={isConfirmModalOpen}
          onClose={() => setIsConfirmModalOpen(false)}
          onConfirm={handleDelete}
          title={confirmConfig.title || "Supprimer le module"}
          message={confirmConfig.message || "Êtes-vous sûr de vouloir supprimer ce module ?"}
          confirmText={confirmConfig.confirmText || "Supprimer"}
          cancelText="Annuler"
          type={confirmConfig.type || "danger"}
          isLoading={deleteLoading}
        />

        {/* Modal de confirmation de suppression d'affectation */}
        <ConfirmationModal 
          isOpen={isModuleClasseConfirmModalOpen}
          onClose={() => setIsModuleClasseConfirmModalOpen(false)}
          onConfirm={handleModuleClasseDelete}
          title={confirmModuleClasseConfig.title || "Supprimer l'affectation"}
          message={confirmModuleClasseConfig.message || "Êtes-vous sûr de vouloir supprimer cette affectation de module ?"}
          confirmText={confirmModuleClasseConfig.confirmText || "Supprimer"}
          cancelText="Annuler"
          type={confirmModuleClasseConfig.type || "danger"}
          isLoading={deleteLoadingModuleClasse}
        />
      </div>
    </Layout>
  );
};

export default ModulePage;
